import React from 'react';
import { createApiKey, fetchApiKeys, revokeApiKey } from '@services/apiKeys';

const GENERIC_ERROR = 'Something went wrong, please try again.';

// Owns all API-key state for the account-settings section: the active-key list,
// load/create/revoke lifecycle, and the one-time reveal of a freshly created
// secret. The secret lives only in `revealKey` and is never refetched.
export const useApiKeys = () => {
  const [keys, setKeys] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);

  const [isCreating, setIsCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState(null);
  const [revealKey, setRevealKey] = React.useState(null);

  const [revokingId, setRevokingId] = React.useState(null);

  React.useEffect(() => {
    const ref = { isCanceled: false };
    const load = async () => {
      const { status, data } = await fetchApiKeys();
      if (ref.isCanceled) return;
      if (status === 200) {
        setKeys(Array.isArray(data) ? data : []);
        setLoadError(null);
      } else {
        setLoadError(GENERIC_ERROR);
      }
      setIsLoading(false);
    };
    load();
    return () => {
      ref.isCanceled = true;
    };
  }, []);

  const clearCreateError = React.useCallback(() => setCreateError(null), []);

  const create = React.useCallback(async ({ name, expires_at }) => {
    setIsCreating(true);
    setCreateError(null);
    const { status, data } = await createApiKey({ name, expires_at });
    setIsCreating(false);
    if (status === 201) {
      // Prepend metadata from the response; the full secret stays in revealKey.
      const { api_key, ...metadata } = data;
      setKeys((prev) => [metadata, ...prev]);
      setRevealKey(data);
      return true;
    }
    // 422 carries a message (missing/duplicate name); anything else falls back.
    setCreateError(data?.error || GENERIC_ERROR);
    return false;
  }, []);

  const dismissReveal = React.useCallback(() => setRevealKey(null), []);

  const revoke = React.useCallback(async (id) => {
    setRevokingId(id);
    const { status } = await revokeApiKey(id);
    setRevokingId(null);
    // 204 = revoked, 404 = already gone -> either way drop it from the list.
    if (status === 204 || status === 404) {
      setKeys((prev) => prev.filter((k) => k.id !== id));
      return true;
    }
    return false;
  }, []);

  return {
    keys,
    isLoading,
    loadError,
    isCreating,
    createError,
    clearCreateError,
    create,
    revealKey,
    dismissReveal,
    revoke,
    revokingId,
  };
};
